export type PhotoCoachExample = {
  id: string;
  slotTitle: string;
  baseName: string;
  variant: "good" | "bad";
  /** Short note on why this example works or should be avoided — editable copy. */
  description: string;
};

export const PHOTO_COACH_EXAMPLE_EXTENSIONS = [
  ".webp",
  ".jpg",
  ".jpeg",
  ".png",
] as const;

export function photoCoachExampleSrc(
  baseName: string,
  extensionIndex: number,
): string {
  const ext =
    PHOTO_COACH_EXAMPLE_EXTENSIONS[extensionIndex] ??
    PHOTO_COACH_EXAMPLE_EXTENSIONS[0];
  return `/images/${baseName}${ext}`;
}

function probePhotoCoachImageUrl(src: string): Promise<boolean> {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = src;
  });
}

/** First matching extension under `public/images/`, or null if missing. */
export async function resolvePhotoCoachExampleUrl(
  baseName: string,
): Promise<string | null> {
  for (let index = 0; index < PHOTO_COACH_EXAMPLE_EXTENSIONS.length; index++) {
    const src = photoCoachExampleSrc(baseName, index);
    if (await probePhotoCoachImageUrl(src)) {
      return src;
    }
  }
  return null;
}

/** Six scrollable examples (good + bad per slot) — assets under `public/images/`. */
export const PHOTO_COACH_EXAMPLES: PhotoCoachExample[] = [
  {
    id: "main-good",
    slotTitle: "Main photo (search cover)",
    baseName: "good-example-1",
    variant: "good",
    description:
      "Whole item in frame, sharp focus, and even lighting so buyers can see condition at a glance.",
  },
  {
    id: "main-bad",
    slotTitle: "Main photo (search cover)",
    baseName: "bad-example-1",
    variant: "bad",
    description:
      "Slanted or cropped image — buyers cannot see the image properly.",
  },
  {
    id: "detail-good",
    slotTitle: "Detail or label",
    baseName: "good-example-2",
    variant: "good",
    description: "Image is clear and detailed, features are visible enough.",
  },
  {
    id: "detail-bad",
    slotTitle: "Detail or label",
    baseName: "bad-example-2",
    variant: "bad",
    description:
      "Item is hidden in a box or bag, making it difficult to see the item clearly.",
  },
  {
    id: "context-good",
    slotTitle: "Angle or context",
    baseName: "good-example-3",
    variant: "good",
    description:
      "Full item is seem clearly from afar but not too far away... adds useful context.",
  },
  {
    id: "context-bad",
    slotTitle: "Angle or context",
    baseName: "bad-example-3",
    variant: "bad",
    description: "Item is crowded by other items.",
  },
];
